import { Info, Lightbulb, TriangleAlert } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'

/** マニュアルの章（目次と本文の両方で使う） */
export interface ManualSectionMeta {
  id: string
  title: string
  icon: ComponentType<{ className?: string }>
}

export function ManualSection({
  section,
  children,
}: {
  section: ManualSectionMeta
  children: ReactNode
}) {
  const Icon = section.icon
  return (
    <section id={section.id} className="scroll-mt-6">
      <div className="mb-4 flex items-center gap-3">
        <Icon className="h-6 w-6 text-indigo-600" />
        <h2 className="text-2xl font-bold text-foreground">{section.title}</h2>
      </div>
      <div className="space-y-6 text-muted-foreground">{children}</div>
    </section>
  )
}

export function ManualSubheading({ children }: { children: ReactNode }) {
  return <h3 className="mb-3 text-lg font-semibold text-foreground">{children}</h3>
}

/** 番号付きの操作手順 */
export function ManualSteps({ steps }: { steps: ReactNode[] }) {
  return (
    <ol className="space-y-3">
      {steps.map((step, index) => (
        <li key={index} className="flex gap-3 text-sm">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
            {index + 1}
          </span>
          <span className="pt-0.5">{step}</span>
        </li>
      ))}
    </ol>
  )
}

export function ManualList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc space-y-1.5 pl-5 text-sm">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  )
}

const CALLOUT_STYLES = {
  info: { icon: Info, className: 'border-blue-400 bg-blue-50 text-blue-900' },
  tip: { icon: Lightbulb, className: 'border-green-400 bg-green-50 text-green-900' },
  warning: { icon: TriangleAlert, className: 'border-amber-400 bg-amber-50 text-amber-900' },
} as const

export function ManualCallout({
  tone = 'info',
  title,
  children,
}: {
  tone?: keyof typeof CALLOUT_STYLES
  title?: string
  children: ReactNode
}) {
  const { icon: Icon, className } = CALLOUT_STYLES[tone]
  return (
    <div className={`rounded border-l-4 p-4 text-sm ${className}`}>
      <div className="flex gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          {title && <p className="font-semibold">{title}</p>}
          <div className="space-y-1">{children}</div>
        </div>
      </div>
    </div>
  )
}

export function ManualTable({ headers, rows }: { headers: string[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-left text-foreground">
          <tr>
            {headers.map((header) => (
              <th key={header} className="whitespace-nowrap px-3 py-2 font-semibold">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-t align-top">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** 画面上のボタン・メニュー名などの UI ラベル */
export function UiLabel({ children }: { children: ReactNode }) {
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-foreground">{children}</span>
  )
}
